import React from 'react'
import { css } from '@emotion/core'
import {
  Avatar,
  Bleed,
  Card,
  colors,
  DataBlock,
  Divider,
  Ellipsis,
  LayoutBand,
  Paragraph,
  Row,
  Separator,
  Skeleton,
} from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'

const factsCss = css`
  max-height: 240px;
  -webkit-mask-image: linear-gradient(180deg, #000 60%, transparent);
  mask-image: linear-gradient(180deg, #000 60%, transparent);
  min-width: 264px;
`

function RelationshipBlock({ relatives, heading }) {
  const { t } = useTranslation()
  return (
    <>
      {!!relatives.length && (
        <DataBlock
          key="heading"
          heading={heading}
          data={
            <div style={{ marginTop: '4px' }}>
              {relatives.map((relative) => (
                <Row key={relative.id || relative.display?.name} alignY="middle" gutters="xxs">
                  <Avatar size="xs" sex={relative.display?.gender?.toLowerCase() || 'unknown'} />
                  <Ellipsis>
                    {relative.display?.name ||
                      t('apac.section-templates.surname-tree-person-section.person-card.unknown-name', 'Unknown')}
                  </Ellipsis>
                </Row>
              ))}
            </div>
          }
        />
      )}
    </>
  )
}

function RelativesBlock({ person }) {
  const spouses = person.spouse || []
  const children = person.children || []
  const parents = person.parent || []
  const { t } = useTranslation()

  const spousesText = t(
    'apac.section-templates.surname-tree-person-section.person-card.spouse-label',
    '{number, plural, one {Spouse} other {Spouses}}',
    { number: spouses.length }
  )
  const parentsText = t(
    'apac.section-templates.surname-tree-person-section.person-card.parent-label',
    '{number, plural, one {Parent} other {Parents}}',
    { number: parents.length }
  )
  const childrenText = t(
    'apac.section-templates.surname-tree-person-section.person-card.child-label',
    '{number, plural, one {Child} other {Children}}',
    { number: children.length }
  )
  const hasRelatives = spouses.length > 0 || children.length > 0 || parents.length > 0

  return (
    <>
      {hasRelatives ? (
        <>
          <RelationshipBlock relatives={spouses} heading={spousesText} />
          <RelationshipBlock relatives={parents} heading={parentsText} />
          <RelationshipBlock relatives={children} heading={childrenText} />
        </>
      ) : (
        <Paragraph size="xs">
          {t('apac.section-templates.surname-tree-person-section.person-card.no-relationships', 'No relationships')}
        </Paragraph>
      )}
    </>
  )
}

export function PersonCardSkeleton() {
  return (
    <Card margins="xs" data-testid="person-card-skeleton">
      <Separator size="nano" />
      <Row alignX="center">
        <Skeleton.Avatar size="lg" />
      </Row>
      <Separator size="xxs" />
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <Skeleton.Paragraph dense lineCount={1} width="60%" />
      </div>
      <Separator size="xxs" />
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <Skeleton.Paragraph dense size="lg" lineCount={1} width="40%" />
      </div>
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <Skeleton.Paragraph size="xs" dense lineCount={1} width="50%" />
      </div>
      <Separator size="xs" />
      <Divider fullWidth />
      <Bleed bottom growY>
        <LayoutBand color={colors.gray02} css={factsCss}>
          <Separator size="xs" />
          <div style={{ display: 'flex', height: '42px', flexDirection: 'column', justifyContent: 'center' }}>
            <Skeleton.Paragraph size="xs" lineCount={1} width="70%" />
          </div>
          <Separator size="xs" />
          <div>
            <Skeleton.Paragraph size="xs" lineCount={2} />
            <Separator size="xxs" />
            <Skeleton.Paragraph size="xs" lineCount={1} width="80%" />
          </div>
          <div>
            <Separator size="xs" />
          </div>
        </LayoutBand>
      </Bleed>
    </Card>
  )
}

export default function PersonCard({ person }) {
  const { t } = useTranslation()

  // Extract given name and surname from the person name
  const nameParts = person.name ? person.name.split(' ') : []
  const givenName =
    nameParts.length > 0
      ? nameParts.slice(0, -1).join(' ') || nameParts[0]
      : t('apac.section-templates.surname-tree-person-section.person-card.unknown-name', 'Unknown')
  const surname = nameParts.length > 1 ? nameParts.at(-1) : ''

  // Use the lifespan from the formatted data or fallback to 'Deceased'
  const lifespan =
    person.lifespan || t('apac.section-templates.surname-tree-person-section.person-card.no-lifespan-data', 'Deceased')

  // Get the first place fact if available
  const placeFact = person.gedcomx?.persons?.[0]?.facts?.find(
    (fact) => fact.place && (fact.type?.includes('Birth') || fact.type?.includes('Christening'))
  )
  const placeText = placeFact?.place?.original || placeFact?.place?.normalized?.[0]?.value

  return (
    <Card margins="xs" to={`https://ancestors.familysearch.org/${person.identifier}`} external>
      <Separator size="nano" />
      <Row alignX="center">
        <Avatar size="lg" sex={person.gender} />
      </Row>
      <Separator size="xxs" />
      <Paragraph centered dense>
        {givenName}
      </Paragraph>
      <Separator size="xxs" />
      <Paragraph centered dense size="lg">
        {surname}
      </Paragraph>
      <div>
        <Paragraph size="xs" centered dense>
          {lifespan}
        </Paragraph>
      </div>
      <Separator size="xs" />
      <Divider fullWidth />
      <Bleed bottom growY>
        <LayoutBand color={colors.gray02} css={factsCss}>
          <Separator size="xs" />
          <div style={{ display: 'flex', height: '42px', flexDirection: 'column', justifyContent: 'center' }}>
            {placeText ? (
              <DataBlock
                key="birthplace"
                heading={t(
                  'apac.section-templates.surname-tree-person-section.person-card.birthplace-label',
                  'Birthplace'
                )}
                data={<Ellipsis>{placeText}</Ellipsis>}
              />
            ) : (
              <Paragraph size="xs">
                {t(
                  'apac.section-templates.surname-tree-person-section.person-card.no-place-data',
                  'No place information'
                )}
              </Paragraph>
            )}
          </div>
          <Separator size="xs" />
          <RelativesBlock person={person} />
          <div>
            <Separator size="xs" />
          </div>
        </LayoutBand>
      </Bleed>
    </Card>
  )
}
